import type { ColTypeDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import type { IUnifiedOrderBlotterEntry } from 'apiConnector/blotters';
import { secondaryOrderBlotterApiConnector } from 'apiConnector/blotters';
import classnames from 'classnames';
import { SecondaryMarketOrderBlotterGrid } from 'components/Blotters/SecondaryMarketOrder/grid/Grid';
import { SecondaryMarketOrderBlotterPagination } from 'components/Blotters/SecondaryMarketOrder/pagination/Pagination';
import { SecondaryMarketOrderBlotterTools } from 'components/Blotters/SecondaryMarketOrder/tools/Tools';
import { PreferencesLoadingErrorModal } from 'components/Blotters/SecondaryMarketTrade/preferencesLoadingErrorModal';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useContextMenuItems } from 'components/shared/Blotter/Grid/Hooks/useContextMenuItems';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useRelativeTimeColumnType } from 'components/shared/Blotter/Grid/Hooks/useRelativeTimeColumnType';
import { useStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SecondaryOrderSidePanel } from 'routes/secondaryMarketOrderBlotter/SidePanel/SecondaryOrderSidePanel';
import { httpClient } from 'utils/http/httpClient';

import { usePercentOrAmountColumnType } from '../../../components/shared/Blotter/Grid/Hooks/usePercentOrAmountColumnType';
import type { ComponentProps } from './SecondaryOrderBlotter.connect';

const getHeaderIntlKey = getHeaderForBlotterIntlKey('secondaryMarketOrderBlotter');

const apiConnected = secondaryOrderBlotterApiConnector(httpClient);

function getRowId(p: GetRowIdParams) {
  return p.data['orderId'];
}

const percentOrAmountPriceType = (data: IUnifiedOrderBlotterEntry) => ({
  priceType: data.priceType,
  currency: data.currency,
});
const amountPriceType = (data: IUnifiedOrderBlotterEntry) => ({ priceType: 'Amount', currency: data.currency });

export function SecondaryOrderBlotter({
  selected,
  isSecondaryMarketPostTradeRedirectionAllowed,
  isSecondaryMarketOrderCancellationAllowed,
  isSecondaryOrderBookEnabled,
  orderInstantBookStatus,
  orderInstantBookError,
  orderCancellationStatus,
  orderCancellationError,
  onReset,
  onOpenSecondaryMarketPostTrade,
  onInstantBookWorkflowInitiated,
  onInstantBookWorkflowConfirmed,
  onInstantBookWorkflowAborted,
  onInstantBookWorkflowCompleted,
  onCancellationWorkflowInitiated,
  onCancellationWorkflowConfirmed,
  onCancellationWorkflowAborted,
  onCancellationWorkflowCompleted,
  onOpenNewOrderTab,
}: ComponentProps) {
  const [gridApi, setGridApi] = useState<GridApi>();

  const onPanelClose = useCallback(() => {
    onReset();
  }, [onReset]);

  useEffect(() => {
    onReset();
  }, [onReset]);

  const defaultColumnDefinition = useDefaultColumnDefinition<IUnifiedOrderBlotterEntry>(getHeaderIntlKey);
  const percentOrAmountColumn = usePercentOrAmountColumnType<IUnifiedOrderBlotterEntry>(percentOrAmountPriceType);
  const amountColumn = usePercentOrAmountColumnType<IUnifiedOrderBlotterEntry>(amountPriceType);
  const statusColumn = useStatusColumnType<string, IUnifiedOrderBlotterEntry>(getStatusFieldColor, true, true);

  const relativetimeColumn = useRelativeTimeColumnType<IUnifiedOrderBlotterEntry>(true);
  const customColumns = useMemo<Record<string, ColTypeDef<IUnifiedOrderBlotterEntry>>>(() => {
    return {
      percentOrAmount: percentOrAmountColumn,
      amount: amountColumn,
      relativeTime: relativetimeColumn,
      status: statusColumn,
    };
  }, [amountColumn, percentOrAmountColumn, relativetimeColumn, statusColumn]);
  const columnTypes = useClientSideDefaultColumnTypes<IUnifiedOrderBlotterEntry>(customColumns, true);

  const onGridReady = useCallback(({ api }: GridReadyEvent) => {
    setGridApi(api);
  }, []);

  const getContextMenuItems = useContextMenuItems('Blotter Secondary Market orders', gridApi);

  return (
    <PreferencesLoadingErrorModal>
      <div
        className="page-container secondary-orders-blotter d-flex flex-column justify-content-between w-100 h-100"
        data-e2e="secondary-market-orders-blotter-container"
      >
        <div className="d-flex flex-column h-100">
          <div className="d-flex flex-row flex-grow-1 min-h-0 vh-100">
            <div
              className={classnames(
                'flex-grow-1 flex-shrink-1 d-flex flex-column p-0 border-top',
                selected ? 'col-md-6 col-lg-8 col-xl-9 col-xxl-9' : 'col-12',
              )}
            >
              <SecondaryMarketOrderBlotterTools fetchCsv={apiConnected.fetchCSV} />
              <SecondaryMarketOrderBlotterGrid
                name="secondaryMarketOrderBlotter"
                e2e="secondary-order-blotter-grid"
                logAnalyticName="Blotter Secondary Market orders"
                getRowId={getRowId}
                columnTypes={columnTypes}
                defaultColDef={defaultColumnDefinition}
                onGridReady={onGridReady}
                getContextMenuItems={getContextMenuItems}
              />
              <SecondaryMarketOrderBlotterPagination />
            </div>
            {selected && (
              <div className="col-md-6 col-lg-4 col-xl-3 col-xxl-3 px-0">
                <SecondaryOrderSidePanel
                  className="h-100 border-start border-top d-flex flex-column"
                  isSecondaryMarketPostTradeRedirectionAllowed={isSecondaryMarketPostTradeRedirectionAllowed}
                  isSecondaryMarketOrderCancellationAllowed={isSecondaryMarketOrderCancellationAllowed}
                  isSecondaryOrderBookEnabled={isSecondaryOrderBookEnabled}
                  selected={selected}
                  onOpenNewOrderTab={onOpenNewOrderTab}
                  onClose={onPanelClose}
                  onOpenSecondaryMarketPostTrade={onOpenSecondaryMarketPostTrade}
                  onInstantBookWorkflowInitiated={onInstantBookWorkflowInitiated}
                  onInstantBookWorkflowConfirmed={onInstantBookWorkflowConfirmed}
                  onInstantBookWorkflowAborted={onInstantBookWorkflowAborted}
                  onInstantBookWorkflowCompleted={onInstantBookWorkflowCompleted}
                  onCancellationWorkflowInitiated={onCancellationWorkflowInitiated}
                  onCancellationWorkflowConfirmed={onCancellationWorkflowConfirmed}
                  onCancellationWorkflowAborted={onCancellationWorkflowAborted}
                  onCancellationWorkflowCompleted={onCancellationWorkflowCompleted}
                  orderInstantBookStatus={orderInstantBookStatus}
                  orderInstantBookError={orderInstantBookError}
                  orderCancellationStatus={orderCancellationStatus}
                  orderCancellationError={orderCancellationError}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PreferencesLoadingErrorModal>
  );
}

SecondaryOrderBlotter.displayName = 'SecondaryOrderBlotter';
